import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLeadModal } from "@/context/LeadModalContext";
import { useSite } from "@/context/SiteContext";
import { Logo } from "./Logo";

export const Nav = ({ categories = [] }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { open: openLeadModal } = useLeadModal();
  const { settings } = useSite();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "platform", label: "Platform", href: "/#platform" },
    { id: "use-cases", label: "Use Cases", href: "/#use-cases" },
    { id: "pricing", label: "Pricing", href: "/pricing", route: true },
    { id: "demo", label: "Demo", href: "/#demo" },
  ];

  return (
    <header
      data-testid="site-nav"
      className="relative z-40 bg-white/95 backdrop-blur-xl border-b border-neutral-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center min-w-0 shrink">
          <Logo size="md" context="nav" />
        </Link>

        <div className="flex items-center gap-4 sm:gap-7 shrink-0 ml-auto">
          <nav className="hidden md:flex items-center gap-7">
            {links.map((l) =>
              l.route ? (
                <Link
                  key={l.id}
                  to={l.href}
                  data-testid={`nav-link-${l.id}`}
                  className="text-sm font-medium text-neutral-700 hover:text-neutral-950 transition-colors"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={l.href}
                  data-testid={`nav-link-${l.id}`}
                  className="text-sm font-medium text-neutral-700 hover:text-neutral-950 transition-colors"
                >
                  {l.label}
                </a>
              )
            )}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              data-testid="nav-cta-demo"
              onClick={() => openLeadModal()}
              className="hidden sm:inline-flex items-center justify-center px-4 py-2 rounded-md bg-brand text-white text-sm font-semibold hover:bg-brand-dark transition-colors"
            >
              {settings?.cta_demo_label || "Request demo"}
            </button>
            <button
              type="button"
              aria-label="Toggle menu"
              data-testid="nav-menu-toggle"
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md border border-neutral-200 text-neutral-800"
              onClick={() => setOpen((v) => !v)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-neutral-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-3">
            {links.map((l) =>
              l.route ? (
                <Link
                  key={l.id}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-link-mobile-${l.id}`}
                  className="text-sm font-medium text-neutral-800"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-link-mobile-${l.id}`}
                  className="text-sm font-medium text-neutral-800"
                >
                  {l.label}
                </a>
              )
            )}
            <div className="pt-2 border-t border-neutral-200">
              <p className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-2">
                Use cases
              </p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                {categories.map((c) => (
                  <a
                    key={c.id}
                    href={`#${c.slug || c.id}`}
                    onClick={() => setOpen(false)}
                    className="text-xs text-neutral-700"
                  >
                    {c.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
